import { useEffect, useState } from "react";
import { HomeframeInput, HomeframeSelect, Link } from "@builtbyted/homeframe";
import {
  Bars,
  Heatmap,
  LineChart,
  sumSeries,
  WorldMap,
  type Location,
  type Series,
} from "./Charts";
import {
  count,
  duration,
  num,
  str,
  timeAgo,
  Page,
  Skeleton,
  Message,
  Poster,
  Section,
  DayPicker,
  TopList,
  scoped,
  useData,
  useLink,
  useNavigateQuery,
  useQuery,
  type Row,
} from "./ui";

interface Trends {
  available: boolean;
  message: string | null;
  generated_at: number;
  step: number;
  hours: number;
  streams: Series[];
  bandwidth: Series[];
  quality: Series[];
  viewers: Series[];
  hosts: Series[];
  locations: Location[];
  leaderboard: Row[];
  unlocated: number;
}
interface InsightsData {
  plays: number;
  duration: number;
  users: number;
  daily: Row[];
  heatmap: Row[];
  platforms: Row[];
  decisions: Row[];
  resolutions: Row[];
  servers: Row[];
  users_top: Row[];
  media: Row[];
  timezone: string;
  grain: string;
}
interface Server {
  id: number;
  name: string;
  kind: string;
  status: string;
  stream_count: number;
  last_success: number;
}
function Stat({
  label,
  value,
  detail,
  tone = "violet",
}: {
  label: string;
  value: string;
  detail?: string | undefined;
  tone?: string;
}) {
  return (
    <article className={`stat-tile ${tone}`}>
      <span>{label}</span>
      {value === "—" ? (
        <span className="skeleton-value" aria-label="Loading" />
      ) : (
        <strong>{value}</strong>
      )}
      {detail && <small>{detail}</small>}
    </article>
  );
}
function Panel({
  title,
  note,
  children,
  className = "",
}: {
  title: string;
  note?: string | undefined;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`viz-panel ${className}`}>
      <div className="viz-heading">
        <h2>{title}</h2>
        {note && <p>{note}</p>}
      </div>
      {children}
    </section>
  );
}
export function Dashboard() {
  const url = useQuery(),
    navigate = useNavigateQuery();
  const hours = url.searchParams.get("hours") || "6";
  const custom = url.searchParams.get("range") === "custom";
  const start = url.searchParams.get("start") || "";
  const end = url.searchParams.get("end") || "";
  const localDay = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const today = localDay(new Date());
  const [fromDay, setFromDay] = useState(
    start || localDay(new Date(Date.now() - 7 * 86400000)),
  );
  const [toDay, setToDay] = useState(end || today);
  useEffect(() => {
    if (start) setFromDay(start);
    if (end) setToDay(end);
  }, [start, end]);
  const customParams =
    custom && start && end
      ? {
          start: String(
            Math.floor(new Date(start + "T00:00:00").getTime() / 1000),
          ),
          end: String(Math.floor(new Date(end + "T23:59:59").getTime() / 1000)),
        }
      : {};
  const trends = useData<Trends>(
    scoped("/dashboard", url, { hours, ...customParams }),
    30000,
  );
  const sessions = useData<Row[]>(scoped("/activity", url), 5000);
  const servers = useData<Server[]>("/servers", 15000);
  const libraries = useData<Row[]>(scoped("/libraries", url), 60000);
  const recent = useData<Row[]>(scoped("/recent", url), 60000);
  const visible =
    servers.data?.filter(
      (s) =>
        !url.searchParams.get("server") ||
        String(s.id) === url.searchParams.get("server"),
    ) || [];
  const live = sessions.data?.filter((s) => !s.stale) || [];
  const stale = sessions.data?.filter((s) => s.stale) || [];
  const t = trends.data;
  const hoursLabel =
    custom && start && end
      ? `${new Date(start + "T00:00:00").toLocaleDateString()} – ${new Date(end + "T00:00:00").toLocaleDateString()}`
      : hours === "0"
        ? "All time"
        : `Last ${num(hours) >= 24 ? `${num(hours) / 24} days` : `${hours} hours`}`;
  const total = sumSeries(t?.streams || [], "All streams");
  const peak =
    total[0]?.points.reduce((m, p) => Math.max(m, p[1] ?? 0), 0) || 0;
  const bands = sumSeries(t?.bandwidth || [], "Total bandwidth");
  return (
    <Page
      title="Dashboard"
      tools={
        <div className="dashboard-tools">
          <span className="live-label">
            <span className="status-dot online" />
            Auto-refresh
          </span>
          <HomeframeSelect
            aria-label="Dashboard time range"
            value={custom ? "custom" : hours}
            onChange={(e) =>
              navigate(
                e.target.value === "custom"
                  ? { range: "custom", start: fromDay, end: toDay }
                  : { hours: e.target.value, range: "", start: "", end: "" },
              )
            }
          >
            {[
              ["1", "Last hour"],
              ["6", "Last 6 hours"],
              ["24", "Last 24 hours"],
              ["168", "Last 7 days"],
              ["720", "Last 30 days"],
              ["0", "All time"],
              ["custom", "Custom range"],
            ].map(([v, l]) => (
              <option value={v} key={v}>
                {l}
              </option>
            ))}
          </HomeframeSelect>
        </div>
      }
    >
      {custom && (
        <form
          className="date-range toolbar"
          onSubmit={(e) => {
            e.preventDefault();
            if (fromDay && toDay && fromDay <= toDay)
              navigate({ range: "custom", start: fromDay, end: toDay });
          }}
        >
          <label>
            From
            <HomeframeInput
              type="date"
              aria-label="Range start"
              value={fromDay}
              max={toDay || today}
              required
              onChange={(e) => setFromDay(e.target.value)}
            />
          </label>
          <label>
            To
            <HomeframeInput
              type="date"
              aria-label="Range end"
              value={toDay}
              min={fromDay}
              max={today}
              required
              onChange={(e) => setToDay(e.target.value)}
            />
          </label>
          <button type="submit">Apply</button>
        </form>
      )}
      {(servers.error || sessions.error || trends.error) && (
        <Message error>
          {servers.error || sessions.error || trends.error}
        </Message>
      )}
      {stale.length > 0 && (
        <Message error>
          {stale.length} sessions have stale data. Live totals exclude them
          until the server reconnects.
        </Message>
      )}
      <div className="stat-grid">
        <Stat
          label="Streaming now"
          value={sessions.data ? count(live.length) : "—"}
          detail={`${visible.filter((s) => s.status === "online").length} of ${visible.length} servers online`}
        />
        <Stat
          label="Bandwidth now"
          value={
            sessions.data
              ? `${(live.reduce((n, s) => n + num(s.bandwidth), 0) / 1000).toFixed(1)}`
              : "—"
          }
          detail="Mbps"
          tone="cyan"
        />
        <Stat
          label="Viewers in period"
          value={t?.available ? count(t.leaderboard.length) : "—"}
          detail={hoursLabel}
          tone="blue"
        />
        <Stat
          label="Peak concurrency"
          value={t?.available ? Number(peak.toFixed(1)).toLocaleString() : "—"}
          detail={
            t
              ? `${t.step >= 60 ? `${Math.round(t.step / 60)}m` : `${t.step}s`} average`
              : undefined
          }
          tone="peach"
        />
      </div>
      {t?.message && <Message error={!t.available}>{t.message}</Message>}
      <div className="dashboard-grid">
        <Panel
          title="Concurrent streams"
          note={
            t
              ? `${t.step >= 60 ? `${Math.round(t.step / 60)}m` : `${t.step}s`} averages`
              : undefined
          }
          className="span-8"
        >
          <LineChart
            loading={!t && !trends.error}
            series={t?.streams || []}
            label="Concurrent streams by server"
          />
        </Panel>
        <Panel
          title="Around the world"
          note={hoursLabel}
          className="span-4 map-panel"
        >
          {!t && !trends.error ? (
            <Skeleton kind="chart" count={1} />
          ) : (
            <WorldMap locations={t?.locations || []} />
          )}
        </Panel>
        <Panel title="Bandwidth" note="Mbps" className="span-8">
          <LineChart
            loading={!t && !trends.error}
            series={bands}
            scale={1000}
            filled
            unit="Mbps"
            label="Stream bandwidth"
          />
        </Panel>
        <Panel title="Stream quality" className="span-6">
          <LineChart
            loading={!t && !trends.error}
            series={t?.quality || []}
            label="Streams by quality"
          />
        </Panel>
        <Panel title="Who's watching" className="span-6">
          <LineChart
            loading={!t && !trends.error}
            series={t?.viewers || []}
            label="Streams by viewer"
          />
        </Panel>
        <Panel title="Servers & media" className="span-8">
          <LineChart
            loading={!t && !trends.error}
            series={t?.hosts || []}
            label="Streams by host and media type"
          />
        </Panel>
        <Panel title="Top viewers" note={hoursLabel} className="span-4">
          <Bars
            loading={!t && !trends.error}
            rows={t?.leaderboard || []}
            metric="value"
            onSelect={(name) =>
              navigate(
                { username: name, page: "", search: "", user: "" },
                "/history",
              )
            }
          />
        </Panel>
      </div>
      <Section
        title="Now playing"
        tools={<Link to={useLink("/activity")}>View activity →</Link>}
      >
        {!sessions.data ? (
          <Skeleton kind="cards" count={2} />
        ) : !sessions.data.length ? (
          <div className="empty-panel">No active streams.</div>
        ) : (
          <div className="now-playing">
            {sessions.data.map((s) => (
              <article
                key={`${s.server_id}:${s.session_key}`}
                className={`playing-card${s.stale ? " stale" : ""}`}
              >
                <Poster
                  server={s.server_id}
                  path={
                    s.media_type === "episode"
                      ? s.grandparent_thumb || s.thumb
                      : s.thumb
                  }
                />
                <div>
                  <span className="eyebrow">{str(s.server_name)}</span>
                  <h3>{str(s.full_title || s.title)}</h3>
                  <p>
                    {str(s.friendly_name || s.user)}{" "}
                    <span>· {s.stale ? "Connection lost" : str(s.state)}</span>
                  </p>
                  <div className="playing-details">
                    <span>
                      {str(s.transcode_decision).replace(
                        "copy",
                        "Direct stream",
                      )}
                    </span>
                    <span>{(num(s.bandwidth) / 1000).toFixed(1)} Mbps</span>
                    <span>{str(s.player)}</span>
                  </div>
                  <progress
                    value={num(s.progress_percent)}
                    max={100}
                    aria-label={`${str(s.title)} playback progress`}
                  />
                  <small>
                    {duration(num(s.view_offset) / 1000)} /{" "}
                    {duration(num(s.duration) / 1000)}
                  </small>
                </div>
              </article>
            ))}
          </div>
        )}
      </Section>
      <Section
        title="Your collection"
        tools={<Link to={useLink("/libraries")}>View libraries →</Link>}
      >
        {!libraries.data && !libraries.error ? (
          <Skeleton kind="cards" />
        ) : (
          <div className="collection-strip">
            {[
              ["show", "TV series"],
              ["movie", "Movies / videos"],
              ["artist", "Music tracks"],
              ["clip", "Clips"],
            ].map(([type, label]) => (
              <article key={type}>
                <strong>
                  {libraries.data
                    ? count(
                        libraries.data
                          .filter((l) => l.section_type === type)
                          .reduce(
                            (n, l) =>
                              n +
                              num(type === "artist" ? l.child_count : l.count),
                            0,
                          ),
                      )
                    : "—"}
                </strong>
                <span>{label}</span>
              </article>
            ))}
          </div>
        )}
      </Section>
      <Section title="Recently added">
        {!recent.data && !recent.error && <Skeleton kind="posters" count={6} />}
        <div className="poster-grid recent-grid">
          {recent.data?.slice(0, 12).map((r, i) => (
            <article key={`${r.server_id}:${r.rating_key}:${i}`}>
              <Poster
                server={r.server_id}
                path={r.thumb}
                title={str(r.title)}
              />
              <h3>{str(r.title)}</h3>
              <small>{str(r.grandparent_title)}</small>
            </article>
          ))}
        </div>
        {recent.error && <Message error>{recent.error}</Message>}
      </Section>
    </Page>
  );
}
export function Insights() {
  const url = useQuery(),
    navigate = useNavigateQuery();
  const metric =
    url.searchParams.get("metric") === "duration" ? "duration" : "plays";
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const data = useData<InsightsData>(
    scoped("/insights", url, {
      days: url.searchParams.get("days") || "30",
      timezone,
    }),
    60000,
  );
  const top = useData<{ movies: Row[]; shows: Row[] }>(
    scoped("/stats", url, { days: url.searchParams.get("days") || "30" }),
    60000,
  );
  const d = data.data;
  const types = [...new Set(d?.daily.map((r) => str(r.media_type)) || [])];
  const days = [...new Set(d?.daily.map((r) => str(r.day)) || [])];
  const daily: Series[] = types.map((type) => ({
    label:
      (
        {
          episode: "TV episodes",
          movie: "Movies",
          track: "Music",
          clip: "Clips",
        } as Record<string, string>
      )[type] || type,
    points: days.map((day) => [
      Date.parse(day + (day.length === 7 ? "-01" : "") + "T12:00:00") / 1000,
      num(
        d?.daily.find((r) => r.day === day && r.media_type === type)?.[metric],
      ),
    ]),
  }));
  return (
    <Page title="Insights" tools={<DayPicker />}>
      {data.error && <Message error>{data.error}</Message>}
      <div className="stat-grid">
        <Stat label="Recorded plays" value={d ? count(d.plays) : "—"} />
        <Stat
          label="Watch time"
          value={d ? duration(d.duration) : "—"}
          tone="cyan"
        />
        <Stat
          label="Active viewers"
          value={d ? count(d.users) : "—"}
          tone="blue"
        />
        <Stat
          label="Average watch"
          value={d ? duration(d.plays ? d.duration / d.plays : 0) : "—"}
          detail="Per play"
          tone="peach"
        />
      </div>
      <div className="toolbar insights-toolbar">
        <div className="segmented">
          {[
            ["plays", "Play count"],
            ["duration", "Watch time"],
          ].map(([m, label]) => (
            <button
              key={m}
              aria-pressed={metric === m}
              onClick={() => navigate({ metric: m === "plays" ? "" : m! })}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="dashboard-grid">
        <Panel
          title={d?.grain === "month" ? "Across the months" : "Day by day"}
          note={
            metric === "plays"
              ? "Recorded plays by media type"
              : "Hours watched by media type"
          }
          className="span-12"
        >
          <LineChart
            loading={!d && !data.error}
            series={daily}
            scale={metric === "duration" ? 3600 : 1}
            unit={metric === "duration" ? "hours" : ""}
            label="Playback history trend"
          />
        </Panel>
        <Panel
          title="Prime time"
          note={timezone.replaceAll("_", " ")}
          className="span-12"
        >
          {!d && !data.error ? (
            <Skeleton kind="chart" count={1} />
          ) : (
            <Heatmap rows={d?.heatmap || []} metric={metric} />
          )}
        </Panel>
        <Panel title="Playback methods" className="span-4">
          <Bars
            loading={!d && !data.error}
            rows={
              d?.decisions.map((r) => ({
                ...r,
                label: str(r.label).replace("copy", "Direct stream"),
              })) || []
            }
            metric={metric}
          />
        </Panel>
        <Panel title="Favorite devices" className="span-4">
          <Bars
            loading={!d && !data.error}
            rows={d?.platforms || []}
            metric={metric}
          />
        </Panel>
        <Panel title="Stream resolutions" className="span-4">
          <Bars
            loading={!d && !data.error}
            rows={d?.resolutions || []}
            metric={metric}
          />
        </Panel>
        <Panel title="Your audience" className="span-6">
          <Bars
            loading={!d && !data.error}
            rows={d?.users_top || []}
            metric={metric}
            onSelect={(name) =>
              navigate(
                { username: name, page: "", search: "", user: "" },
                "/history",
              )
            }
          />
        </Panel>
        <Panel title="Across your servers" className="span-6">
          <Bars
            loading={!d && !data.error}
            rows={d?.servers || []}
            metric={metric}
          />
        </Panel>
      </div>
      <div className="stats-grid favorites">
        <TopList title="Most watched movies" rows={top.data?.movies} posters />
        <TopList title="Most watched TV shows" rows={top.data?.shows} posters />
      </div>
    </Page>
  );
}
